/* -- File Input component ----------------------------------------- */
// The plain .file-input is CSS only. This module drives the .file-drop zone:
// the native <input type="file"> covers the card, so clicking and dropping
// are the browser's own; on top of that it highlights a drag, filters the
// files by accept / data-max-size / data-max-files (a DROP bypasses accept),
// appends to a multiple selection instead of replacing it, lists the files
// (image previews, Intl-formatted sizes, remove buttons) and exposes the
// named State API (AGENTS.md "State API").

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals } from '../../shared/state-api.js';

const df$ = defussGlobals();

/** default = empty; dragover = files hover the zone; selected = files
 * chosen; error = some files were rejected (config.message). */
const fileInputStates = ['default', 'dragover', 'selected', 'error'];

const inputOf = (el) => el.querySelector('.file-drop-input');

/** Does a file match the input's accept list (".pdf", "image/*", "image/png")? */
function accepts(input, file) {
  const list = (input.accept || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (!list.length) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return list.some((a) => (a.startsWith('.') ? name.endsWith(a) : a.endsWith('/*') ? type.startsWith(a.slice(0, -1)) : type === a));
}

const lang = (el) => el.closest('[lang]')?.lang || undefined;

/** 1536 → "1.5 kB" (Intl unit formatting, the locale's own). */
function formatSize(el, bytes) {
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte'];
  let i = 0;
  let n = bytes;
  while (n >= 1000 && i < units.length - 1) {
    n /= 1000;
    i++;
  }
  return new Intl.NumberFormat(lang(el), { style: 'unit', unit: units[i], unitDisplay: 'short', maximumFractionDigits: i ? 1 : 0 }).format(n);
}

const ICON_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

/** Rebuild input.files from a File[] (DataTransfer is the only way to set it). */
function setFiles(input, files) {
  const dt = new DataTransfer();
  for (const f of files) dt.items.add(f);
  input.files = dt.files;
}

function renderList(el) {
  const input = inputOf(el);
  const list = el.querySelector('.file-drop-list');
  if (!list) return;
  (el._urls || []).forEach((u) => URL.revokeObjectURL(u));
  el._urls = [];
  list.replaceChildren(
    ...[...input.files].map((file, i) => {
      const li = document.createElement('li');
      li.className = 'file-drop-item';
      const thumb = document.createElement('span');
      thumb.className = 'file-drop-thumb';
      thumb.setAttribute('aria-hidden', 'true');
      if (file.type.startsWith('image/')) {
        const img = document.createElement('img');
        img.alt = '';
        img.src = URL.createObjectURL(file);
        el._urls.push(img.src);
        thumb.append(img);
      } else {
        thumb.textContent = (file.name.split('.').pop() || 'file').slice(0, 4);
      }
      const name = document.createElement('div');
      name.className = 'file-drop-name';
      name.textContent = file.name;
      const meta = document.createElement('span');
      meta.className = 'file-drop-meta';
      meta.textContent = formatSize(el, file.size);
      name.append(meta);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'file-drop-remove';
      remove.innerHTML = ICON_X;
      remove.setAttribute('aria-label', `Remove ${file.name}`);
      remove.addEventListener('click', () => {
        setFiles(input, [...input.files].filter((_, k) => k !== i));
        el._kept = [...input.files];
        apply(el, [], true);
        // tell form listeners - flagged, so our own change handler skips it
        el._removing = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        el._removing = false;
      });
      li.append(thumb, name, remove);
      return li;
    }),
  );
}

/** Show the rejections (or clear them) and settle the state name. */
function apply(el, rejected, quiet = false) {
  const input = inputOf(el);
  const err = el.querySelector('.file-drop-error');
  const message = rejected.length ? `Not added: ${rejected.map((r) => `${r.file.name} (${r.why})`).join(', ')}` : '';
  if (err) {
    err.textContent = message;
    err.setAttribute('role', 'alert');
  }
  el.dataset.stateName = rejected.length ? 'error' : input.files.length ? 'selected' : 'default';
  renderList(el);
  if (!quiet && rejected.length) el.dispatchEvent(new CustomEvent('file-drop:rejected', { bubbles: true, detail: { files: rejected.map((r) => r.file), message } }));
}

/** Merge the new pick into the kept files, filter, write input.files back. */
function onPick(el) {
  const input = inputOf(el);
  const incoming = [...input.files];
  const kept = input.multiple ? el._kept || [] : [];
  const maxSize = parseFloat(el.dataset.maxSize || 'Infinity');
  const maxFiles = input.multiple ? parseFloat(el.dataset.maxFiles || 'Infinity') : 1;
  const key = (f) => `${f.name}|${f.size}|${f.lastModified}`;
  const seen = new Set(kept.map(key));
  const out = [...kept];
  const rejected = [];
  for (const file of incoming) {
    if (seen.has(key(file))) continue;
    if (!accepts(input, file)) rejected.push({ file, why: 'type' });
    else if (file.size > maxSize) rejected.push({ file, why: `over ${formatSize(el, maxSize)}` });
    else if (out.length >= maxFiles) rejected.push({ file, why: `max ${maxFiles}` });
    else {
      out.push(file);
      seen.add(key(file));
    }
  }
  setFiles(input, out);
  el._kept = out;
  apply(el, rejected);
}

function triggerStateChange(el, stateName, config) {
  const input = inputOf(el);
  switch (stateName) {
    case 'default':
      setFiles(input, []);
      el._kept = [];
      apply(el, [], true);
      break;
    case 'dragover':
      el.dataset.stateName = 'dragover';
      break;
    case 'selected': {
      // config.files: [{ name, size, type }] - placeholder files (demos, tests)
      const files = (config?.files || [{ name: 'report.pdf', size: 248000, type: 'application/pdf' }]).map(
        // real bytes (capped at 5 MB) so size, type and name read true
        (f) => new File([new Uint8Array(Math.min(f.size ?? 0, 5e6))], f.name, { type: f.type || '' }),
      );
      setFiles(input, files);
      el._kept = [...input.files];
      apply(el, [], true);
      break;
    }
    case 'error': {
      const err = el.querySelector('.file-drop-error');
      if (err) err.textContent = config?.message || 'Not added: archive.zip (type)';
      el.dataset.stateName = 'error';
      break;
    }
  }
}

/** Registry-level API; pass the .file-drop explicitly. Unknown names throw. */
export const fileInputApi = {
  setState(el, stateName, config = {}) {
    if (!fileInputStates.includes(stateName)) {
      throw new Error(`file-input: unknown state "${stateName}" (supported: ${fileInputStates.join(', ')})`);
    }
    el._stateConfig = config;
    triggerStateChange(el, stateName, config);
  },
  getState(el) {
    const input = inputOf(el);
    return {
      name: el.dataset.stateName || 'default',
      config: { ...el._stateConfig, count: input.files.length, files: [...input.files].map((f) => f.name) },
    };
  },
};

df$.fileInputApi = fileInputApi;
df$.fileInputStates = fileInputStates;

function init() {
  document.querySelectorAll('.file-drop:not([data-init])').forEach((el) => {
    const input = inputOf(el);
    if (!input) return;
    el.dataset.init = '';
    el.dataset.stateName = 'default';
    el._kept = [];
    el.api = {
      setState: (stateName, config) => fileInputApi.setState(el, stateName, config),
      getState: () => fileInputApi.getState(el),
    };
    const zone = el.querySelector('.file-drop-zone') || el;
    let depth = 0; // dragenter/leave fire for every child - count them
    zone.addEventListener('dragenter', (e) => {
      if (!e.dataTransfer?.types.includes('Files') || input.disabled) return;
      depth++;
      el._before = el.dataset.stateName === 'dragover' ? el._before : el.dataset.stateName;
      el.dataset.stateName = 'dragover';
    });
    zone.addEventListener('dragleave', () => {
      depth = Math.max(0, depth - 1);
      if (!depth && el.dataset.stateName === 'dragover') el.dataset.stateName = el._before || 'default';
    });
    zone.addEventListener('drop', () => {
      depth = 0;
      if (el.dataset.stateName === 'dragover') el.dataset.stateName = el._before || 'default';
    });
    input.addEventListener('change', () => {
      // our own re-dispatch after a remove carries no new pick
      if (!el._removing) onPick(el);
    });
  });
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
